import AssignmentIcon from '@mui/icons-material/Assignment';
import ChecklistIcon from '@mui/icons-material/Checklist';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import PolicyIcon from '@mui/icons-material/Policy';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';

import { getCourseOptions } from '@drivingdoc/api-client/generated/react-query';

import UpdateCourseCompletionCriteriaDialog from 'components/Dialogs/Courses/UpdateCourseCompletionCriteriaDialog';
import UpdateCourseEnrollmentCriteriaDialog from 'components/Dialogs/Courses/UpdateCourseEnrollmentCriteriaDialog';
import { UpdateCoursePolicyDialog } from 'components/Dialogs/Courses/UpdateCoursePolicyDialog';
import UpdateCourseSelfScheduleCriteriaDialog from 'components/Dialogs/Courses/UpdateCourseSelfScheduleCriteriaDialog';
import { PageHeaderToolbar } from 'components/PageContainer/PageHeaderToolbar';

import { useMenuControl } from 'hooks/useMenuControl';

interface CourseSettingsMenuProps {
  courseId: string;
}

export function CourseSettingsMenu({ courseId }: CourseSettingsMenuProps) {
  const courseSettingsMenu = useMenuControl();
  const dialogs = useDialogs();
  const course = useQuery({
    ...getCourseOptions({ path: { id: courseId } }),
    enabled: !!courseId,
  });

  return (
    <PageHeaderToolbar>
      <Tooltip title="Course Settings">
        <IconButton aria-label="options" size="large" onClick={courseSettingsMenu.openMenu} sx={{ py: 0 }}>
          <MoreVertIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Menu
        {...courseSettingsMenu.menuProps}
        onClick={courseSettingsMenu.closeMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        sx={{ mt: 1 }}
      >
        <MenuItem onClick={() => dialogs.open(UpdateCourseEnrollmentCriteriaDialog, { courseId: courseId })}>
          <ListItemIcon>
            <AssignmentIcon />
          </ListItemIcon>
          Edit Enrollment Requirements
        </MenuItem>

        <MenuItem onClick={() => dialogs.open(UpdateCoursePolicyDialog, { courseId: courseId })}>
          <ListItemIcon>
            <PolicyIcon />
          </ListItemIcon>
          Edit Course Policy
        </MenuItem>

        {/* only show option when course schedule type is self scheduled */}
        {course.data?.schedule_type === 'self_scheduled' && (
          <MenuItem onClick={() => dialogs.open(UpdateCourseSelfScheduleCriteriaDialog, { courseId: courseId })}>
            <ListItemIcon>
              <PermContactCalendarIcon />
            </ListItemIcon>
            Edit Student Scheduling Policy
          </MenuItem>
        )}

        <MenuItem onClick={() => dialogs.open(UpdateCourseCompletionCriteriaDialog, { courseId: courseId })}>
          <ListItemIcon>
            <ChecklistIcon />
          </ListItemIcon>
          Edit Course Completion Requirements
        </MenuItem>
      </Menu>
    </PageHeaderToolbar>
  );
}

export default CourseSettingsMenu;
